import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Pencil, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { getAdminClients, createClient, updateClient, deleteClient, uploadFile } from '../../services/api';
import { toast } from 'sonner';

const ClientsAdmin = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', industry: '', logo: '', website_url: '', order: 0 });
  const [logoFile, setLogoFile] = useState(null);

  const fetchData = async () => {
    try {
      const res = await getAdminClients();
      setClients(res.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', industry: '', logo: '', website_url: '', order: 0 });
    setLogoFile(null);
    setDialogOpen(true);
  };

  const openEdit = (client) => {
    setEditing(client);
    setForm({
      name: client.name || '',
      industry: client.industry || '',
      logo: client.logo || '',
      website_url: client.website_url || '',
      order: client.order || 0
    });
    setLogoFile(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let logoUrl = form.logo;
      if (logoFile) {
        const uploadRes = await uploadFile(logoFile, 'client');
        logoUrl = uploadRes.data.url;
      }
      const payload = { ...form, logo: logoUrl };
      if (editing) {
        await updateClient(editing.id, payload);
        toast.success('Client updated');
      } else {
        await createClient(payload);
        toast.success('Client added');
      }
      setDialogOpen(false);
      setForm({ name: '', industry: '', logo: '', website_url: '', order: 0 });
      setLogoFile(null);
      setEditing(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this client?')) return;
    try {
      await deleteClient(id);
      toast.success('Client removed');
      fetchData();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-6" data-testid="clients-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
          <p className="text-sm text-gray-500">{clients.length} clients</p>
        </div>
        <Button onClick={openCreate} className="bg-[#2E3A6F]" data-testid="add-client-btn">
          <Plus size={16} className="mr-2" /> Add Client
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="p-4 h-24" /></Card>)}</div>
      ) : clients.length === 0 ? (
        <Card className="border-dashed"><CardContent className="p-12 text-center text-gray-500">No clients added yet</CardContent></Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {clients.map((client) => (
            <Card key={client.id} className="hover:shadow-sm transition-shadow" data-testid={`client-${client.id}`}>
              <CardContent className="p-4 text-center space-y-2">
                {client.logo ? (
                  <img src={client.logo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${client.logo}` : client.logo} alt={client.name} className="h-16 mx-auto object-contain" />
                ) : (
                  <div className="w-16 h-16 mx-auto rounded-lg bg-[#2E3A6F]/10 flex items-center justify-center text-[#2E3A6F] font-bold text-sm">
                    {client.name?.substring(0, 2).toUpperCase()}
                  </div>
                )}
                <h3 className="font-semibold text-sm text-gray-900">{client.name}</h3>
                <p className="text-xs text-gray-500">{client.industry}</p>
                {client.website_url && (
                  <a href={client.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#2E3A6F] hover:underline inline-flex items-center gap-1">
                    <ExternalLink size={10} /> Website
                  </a>
                )}
                <div className="flex gap-1 justify-center mt-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(client)} className="text-xs flex-1">
                    <Pencil size={12} className="mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(client.id)} className="text-red-600 hover:bg-red-50 text-xs">
                    <Trash2 size={12} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Client</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Company Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="client-name-input" /></div>
            <div><Label>Industry *</Label><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} required placeholder="e.g. Automotive, Manufacturing" data-testid="client-industry-input" /></div>
            <div>
              <Label>Website URL</Label>
              <Input value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} placeholder="https://www.example.com" data-testid="client-website-input" />
            </div>
            <div>
              <Label>Logo (200x100 px, PNG recommended)</Label>
              <Input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files[0])} className="text-sm" data-testid="client-logo-upload" />
              {editing?.logo && !logoFile && (
                <div className="mt-2">
                  <img src={editing.logo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${editing.logo}` : editing.logo} alt="Current logo" className="h-10 object-contain" />
                </div>
              )}
              {logoFile && <p className="text-[10px] text-green-600 mt-1">Selected: {logoFile.name}</p>}
            </div>
            <div><Label>Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} /></div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-[#2E3A6F]" data-testid="client-submit-btn">{editing ? 'Update' : 'Add'} Client</Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsAdmin;
